import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'motion/react';
import Lenis from 'lenis';
import {
  fetchCatalogDocuments,
  fetchLayout,
  fetchLayouts,
  type CatalogDocumentsResponse,
  type LayoutDetail,
  type LayoutListItem,
} from '../lib/api';
import { formatCode, formatDimensions } from '../lib/format';
import { BomTable } from '../components/BomTable';
import { PlaceholderRender } from '../components/PlaceholderRender';
import { Lightbox } from '../components/Lightbox';
import { AddToDeckButton } from '../components/AddToDeckButton';
import { useDeck } from '../lib/deckContext';
import { GRID_RENDER_KINDS, DECK_RENDER_KINDS, RENDER_LABELS, type RenderKind } from '../lib/deck';

export function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Filters are driven from the header (Search by Product / Filter by Space)
  // via URL params so the header and gallery stay in sync.
  const spacesParam = searchParams.get('spaces') ?? '';
  const productParam = searchParams.get('product') ?? '';

  const [layouts, setLayouts] = useState<LayoutListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogDocumentsResponse | null>(null);
  // "Busy" = more people / busier scenes → the with-people catalog variant.
  const [busy, setBusy] = useState(false);

  const copyLink = async (key: string, url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(key);
      setTimeout(() => setCopiedLink(null), 1800);
    } catch {
      // Clipboard not available
    }
  };

  // Index of the currently-focused (centered) layout, or null when the cover
  // is in view.
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const focusedIdxRef = useRef<number | null>(null);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lenisRef = useRef<Lenis | null>(null);

  // Set to true when familySlug changes while the user was already browsing
  // layouts (not parked on the cover). After the new layouts render, we land
  // on the first layout of the new filter instead of resetting to the top.
  const wantsFirstLayoutOnLoadRef = useRef(false);

  // Deep-link support: `/?layout=<code>` scrolls to and focuses that layout on
  // load. Captured once on mount so later filter changes don't re-trigger it.
  const initialLayoutParamRef = useRef(searchParams.get('layout'));

  // Which golden catalog PDFs the Visualizer has published (drives the
  // Photorealistic download card). Non-fatal — unpublished variants stay "soon".
  useEffect(() => {
    fetchCatalogDocuments()
      .then(setCatalog)
      .catch(() => {/* leave null → all variants show as coming soon */});
  }, []);

  useEffect(() => {
    let cancelled = false;
    wantsFirstLayoutOnLoadRef.current = focusedIdxRef.current !== null;

    setLayouts(null);
    focusedIdxRef.current = null;
    setFocusedIdx(null);
    const spaces = spacesParam.split(',').filter(Boolean);
    const productId = productParam ? Number(productParam) : undefined;
    fetchLayouts(spaces.length ? spaces : undefined, productId)
      .then((r) => !cancelled && setLayouts(r.layouts))
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [spacesParam, productParam]);

  useEffect(() => {
    const wrapper = scrollerRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    const lenis = new Lenis({
      wrapper,
      content,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
      lerp: 0.08,
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    lenisRef.current = lenis;

    let rafId = requestAnimationFrame(function tick(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(tick);
    });

    let isSnapping = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const targetForSection = (section: HTMLElement, viewportH: number): number => {
      const raw = section.offsetTop + (section.clientHeight - viewportH) / 2;
      return Math.max(0, Math.min(lenis.limit, raw));
    };

    const findNearestSnapTarget = (currentScroll: number): number => {
      const sections = content.querySelectorAll<HTMLElement>('[data-snap-page]');
      const viewportH = wrapper.clientHeight;
      let best = currentScroll;
      let bestDist = Infinity;
      sections.forEach((section) => {
        const target = targetForSection(section, viewportH);
        const dist = Math.abs(currentScroll - target);
        if (dist < bestDist) {
          bestDist = dist;
          best = target;
        }
      });
      return best;
    };

    const updateFocused = (currentScroll: number): void => {
      const layoutSections = content.querySelectorAll<HTMLElement>('[data-layout-index]');
      if (layoutSections.length === 0) {
        if (focusedIdxRef.current !== null) {
          focusedIdxRef.current = null;
          setFocusedIdx(null);
        }
        return;
      }
      const viewportH = wrapper.clientHeight;
      const viewportCenter = currentScroll + viewportH / 2;
      let bestIdx = -1;
      let bestDist = Infinity;
      layoutSections.forEach((sec) => {
        const center = sec.offsetTop + sec.clientHeight / 2;
        const dist = Math.abs(center - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = parseInt(sec.dataset.layoutIndex ?? '0', 10);
        }
      });
      // If the closest layout is still more than ~half a viewport away,
      // the user is up on the cover — no layout focused.
      if (bestDist > viewportH * 0.55 || bestIdx < 0) {
        if (focusedIdxRef.current !== null) {
          focusedIdxRef.current = null;
          setFocusedIdx(null);
        }
        return;
      }
      if (bestIdx !== focusedIdxRef.current) {
        focusedIdxRef.current = bestIdx;
        setFocusedIdx(bestIdx);
      }
    };

    const snapToNearest = (): void => {
      if (isSnapping) return;
      const viewportH = wrapper.clientHeight;
      if (viewportH === 0) return;
      const current = lenis.scroll;
      const target = findNearestSnapTarget(current);
      const distance = Math.abs(current - target);
      if (distance < 1.5) return;
      const duration = Math.min(0.8, 0.35 + (distance / viewportH) * 0.4);
      isSnapping = true;
      lenis.scrollTo(target, {
        duration,
        easing: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
        onComplete: () => {
          isSnapping = false;
        },
      });
    };

    const onScroll = (event: { velocity: number; scroll: number }): void => {
      updateFocused(event.scroll);
      if (isSnapping) return;
      if (Math.abs(event.velocity) > 1.4) {
        if (idleTimer) clearTimeout(idleTimer);
        return;
      }
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(snapToNearest, 0);
    };

    const onUserInput = (): void => {
      isSnapping = false;
      if (idleTimer) clearTimeout(idleTimer);
    };

    lenis.on('scroll', onScroll);
    wrapper.addEventListener('wheel', onUserInput, { passive: true });
    wrapper.addEventListener('touchstart', onUserInput, { passive: true });

    // Initial focus calculation (in case content is already at a snap point)
    updateFocused(lenis.scroll);

    return () => {
      cancelAnimationFrame(rafId);
      if (idleTimer) clearTimeout(idleTimer);
      wrapper.removeEventListener('wheel', onUserInput);
      wrapper.removeEventListener('touchstart', onUserInput);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // After new layouts populate (either initial load or filter change), scroll
  // to either the cover (top) or the first layout of the new filter, based on
  // whether the user was already browsing when they switched.
  useEffect(() => {
    if (!layouts) return;
    const wrapper = scrollerRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    // Wait one frame so React commits the new DOM and Lenis can re-measure.
    requestAnimationFrame(() => {
      const lenis = lenisRef.current;
      if (!lenis) return;
      lenis.resize();

      // Deep link: `/?layout=<code>` lands directly on that layout (highest
      // priority, consumed once). Then strip the param so the URL is clean and
      // later filter changes behave normally.
      const deepLinkCode = initialLayoutParamRef.current;
      if (deepLinkCode) {
        initialLayoutParamRef.current = null;
        setSearchParams(
          (p) => {
            p.delete('layout');
            return p;
          },
          { replace: true },
        );
        const idx = layouts.findIndex((l) => l.code === deepLinkCode);
        if (idx >= 0) {
          const section = content.querySelector<HTMLElement>(`[data-layout-index="${idx}"]`);
          if (section) {
            const viewportH = wrapper.clientHeight;
            const target = Math.max(0, Math.min(lenis.limit, section.offsetTop + (section.clientHeight - viewportH) / 2));
            lenis.scrollTo(target, { immediate: true });
            return;
          }
        }
      }

      // Restore scroll position when returning from a layout detail page.
      const returnCode = sessionStorage.getItem('gallery:returnTo');
      if (returnCode) {
        sessionStorage.removeItem('gallery:returnTo');
        const returnIdx = layouts.findIndex((l) => l.code === returnCode);
        if (returnIdx >= 0) {
          const section = content.querySelector<HTMLElement>(`[data-layout-index="${returnIdx}"]`);
          if (section) {
            const viewportH = wrapper.clientHeight;
            const target = Math.max(0, Math.min(lenis.limit, section.offsetTop + (section.clientHeight - viewportH) / 2));
            lenis.scrollTo(target, { immediate: true });
            return;
          }
        }
      }

      const wantsFirstLayout = wantsFirstLayoutOnLoadRef.current && layouts.length > 0;
      wantsFirstLayoutOnLoadRef.current = false;

      if (wantsFirstLayout) {
        const firstLayout = content.querySelector<HTMLElement>('[data-layout-index="0"]');
        if (firstLayout) {
          const viewportH = wrapper.clientHeight;
          const target = Math.max(
            0,
            Math.min(
              lenis.limit,
              firstLayout.offsetTop + (firstLayout.clientHeight - viewportH) / 2,
            ),
          );
          lenis.scrollTo(target, { duration: 0.7 });
          return;
        }
      }
      lenis.scrollTo(0, { duration: 0.7 });
    });
  }, [layouts]);

  // Cover CTA: smooth-scroll to the first layout (centered), same target math
  // as the scroll-restore effect.
  const scrollToFirstLayout = (): void => {
    const lenis = lenisRef.current;
    const wrapper = scrollerRef.current;
    const content = contentRef.current;
    if (!lenis || !wrapper || !content) return;
    const section = content.querySelector<HTMLElement>('[data-layout-index="0"]');
    if (!section) return;
    const viewportH = wrapper.clientHeight;
    const target = Math.max(0, Math.min(lenis.limit, section.offsetTop + (section.clientHeight - viewportH) / 2));
    lenis.scrollTo(target, { duration: 0.9 });
  };

  const focusedLayout =
    layouts && focusedIdx !== null ? (layouts[focusedIdx] ?? null) : null;

  return (
    <div className="h-full grid grid-cols-2">
      {/* LEFT: scrolling flipbook */}
      <div ref={scrollerRef} className="h-full overflow-y-auto bg-vari-beige/40">
        <div ref={contentRef}>
          {/* Cover */}
          <section
            data-snap-page
            className="h-[100dvh] min-h-[600px] flex items-center justify-center px-6"
          >
            <div className="max-w-xl w-full space-y-6">
              {/* Header */}
              <div className="text-center border-b border-vari-soft-gray/60 pb-4">
                <p className="text-xs uppercase tracking-[0.3em] text-vari-blue-accessible">
                  Vari Idea Starter
                </p>
              </div>

              {/* Downloads */}
              <div>
                <p className="text-center text-[11px] uppercase tracking-[0.25em] text-vari-gray mb-3">
                  Downloads
                </p>
                <div className="grid grid-cols-2 gap-3 text-left">
                  <CatalogDownloadBox
                    title={busy ? 'Photorealistic · Busy' : 'Photorealistic'}
                    desc={
                      busy
                        ? 'Busier scenes with more people.'
                        : 'AI-rendered catalog.'
                    }
                    copiedKeyPrefix="pr"
                    copiedLink={copiedLink}
                    onCopy={copyLink}
                    downloadName={busy ? 'Idea Starter — Photorealistic (Busy).pdf' : 'Idea Starter — Photorealistic.pdf'}
                    headerRight={<BusyToggle busy={busy} onToggle={() => setBusy((b) => !b)} />}
                    tierFor={(pricing) => {
                      const v = catalog?.variants.find((d) => d.people === busy && d.pricing === pricing);
                      return { available: !!(v?.available && v.url), url: v?.url ?? null };
                    }}
                  />
                  <CatalogDownloadBox
                    title="Classic version"
                    desc="Updated Idea Starter in its original format."
                    copiedKeyPrefix="classic"
                    copiedLink={copiedLink}
                    onCopy={copyLink}
                    downloadName="Idea Starter — Classic.pdf"
                    tierFor={(pricing) => {
                      const t = pricing ? catalog?.classic.priced : catalog?.classic.unpriced;
                      return { available: !!(t?.available && t.url), url: t?.url ?? null };
                    }}
                  />
                </div>
              </div>

              {/* Browse CTA — the heading itself scrolls to the first layout */}
              <div className="border-t border-vari-soft-gray/60 pt-6 text-center space-y-3">
                <button
                  type="button"
                  onClick={scrollToFirstLayout}
                  className="inline-flex items-center gap-2 rounded-full bg-vari-navy text-white px-8 py-3 text-base font-semibold hover:bg-vari-blue-accessible transition-colors shadow-sm"
                >
                  Browse the catalog
                  <span aria-hidden>↓</span>
                </button>
                <p className="text-vari-gray text-sm max-w-lg mx-auto">
                  View 82 pre-designed office layouts across six families. Users can customize and
                  create their own decks! Scroll to flip through pages — details appear on the right.
                </p>
              </div>
            </div>
          </section>

          {error && (
            <section
              data-snap-page
              className="h-[100dvh] flex items-center justify-center px-6"
            >
              <div className="rounded-md border border-vari-red bg-vari-red/10 text-vari-red px-6 py-4 text-sm">
                {error}
              </div>
            </section>
          )}

          {layouts === null ? null : layouts.length === 0 ? (
            <section
              data-snap-page
              className="h-[100dvh] flex items-center justify-center"
            >
              <p className="text-vari-gray">No layouts found.</p>
            </section>
          ) : (
            <div className="pt-[4vh] pb-[44vh]">
              {layouts.map((layout, i) => (
                <LayoutPage
                  key={layout.id}
                  layout={layout}
                  index={i}
                  pageIndex={i + 1}
                  totalPages={layouts.length}
                  scrollerRef={scrollerRef}
                />
              ))}
            </div>
          )}
        </div>
      </div>{/* end scrollerRef */}

      {/* RIGHT: focused-layout detail panel (static, no scroll) */}
      <FocusPanel layout={focusedLayout} />
    </div>
  );
}

/** BUSY toggle pill (Photorealistic box). On = more people / busier scenes.
 * Shows a small caption so users know what it does before we train them. */
function BusyToggle({ busy, onToggle }: { busy: boolean; onToggle: () => void }) {
  return (
    <div className="shrink-0 flex flex-col items-end gap-0.5">
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={busy}
        title="Add people and activity to every scene"
        className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors ${
          busy ? 'bg-vari-navy text-white' : 'bg-vari-soft-gray/60 text-vari-gray hover:bg-vari-soft-gray'
        }`}
      >
        Busy
      </button>
      <span className="text-[9px] leading-none text-vari-gray/70">
        {busy ? 'people added' : 'adds people'}
      </span>
    </div>
  );
}

/**
 * One catalog download box (Photorealistic or Classic). Each pricing tier
 * (with / without pricing) exposes two actions: "Create share link" (copies the
 * permanent link) and "Export PDF" (opens/downloads the PDF). `tierFor` resolves
 * a pricing tier to its availability + relative URL; unavailable tiers render as
 * dimmed "coming soon" placeholders.
 */
function CatalogDownloadBox({
  title,
  desc,
  tierFor,
  copiedKeyPrefix,
  copiedLink,
  onCopy,
  headerRight,
  downloadName,
}: {
  title: string;
  desc: string;
  tierFor: (pricing: boolean) => { available: boolean; url: string | null };
  copiedKeyPrefix: string;
  copiedLink: string | null;
  onCopy: (key: string, url: string) => void;
  headerRight?: ReactNode;
  /** When set, "Export PDF" downloads the file with this name instead of
   * opening it in a new tab. (Same-origin only — our /download route is.) */
  downloadName?: string;
}) {
  const btnClass =
    'rounded border border-vari-navy/60 text-vari-navy text-[11px] font-medium px-2 py-1.5 text-center hover:bg-vari-navy hover:text-white transition-colors';
  const disabledClass =
    'rounded border border-dashed border-vari-soft-gray text-vari-gray/45 text-[11px] px-2 py-1.5 text-center cursor-not-allowed';
  return (
    <div className="rounded-lg border border-vari-soft-gray p-4 space-y-3">
      <div className="flex items-start justify-between gap-2 min-h-[3.25rem]">
        <div>
          <p className="text-xs font-semibold text-vari-navy">{title}</p>
          <p className="text-[11px] text-vari-gray leading-snug">{desc}</p>
        </div>
        {headerRight}
      </div>
      {[
        { pricing: true, label: 'With pricing' },
        { pricing: false, label: 'Without pricing' },
      ].map((tier) => {
        const t = tierFor(tier.pricing);
        const key = `${copiedKeyPrefix}-${tier.pricing ? 'priced' : 'unpriced'}`;
        return (
          <div key={tier.label} className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-vari-gray/60">{tier.label}</p>
            {t.available && t.url ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onCopy(key, `${window.location.origin}${t.url}`)}
                  className={btnClass}
                >
                  {copiedLink === key ? '✓ Link copied' : 'Create share link'}
                </button>
                <a
                  href={t.url}
                  {...(downloadName
                    ? { download: downloadName }
                    : { target: '_blank', rel: 'noreferrer' })}
                  className={btnClass}
                >
                  Export PDF
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <span title="Coming soon" className={disabledClass}>Create share link</span>
                <span title="Coming soon" className={disabledClass}>Export PDF</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LayoutPage({
  layout,
  index,
  pageIndex,
  totalPages,
  scrollerRef,
}: {
  layout: LayoutListItem;
  index: number;
  pageIndex: number;
  totalPages: number;
  scrollerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    container: scrollerRef,
    target: ref,
    offset: ['start end', 'end start'],
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [10, 0, -10]);
  const y = useTransform(scrollYProgress, [0, 0.5, 1], [12, 0, -12]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 0.8, 1], [0.35, 0.8, 1, 0.8, 0.35]);
  // Scale falloff is gentle now — neighbors stay nearly the same size as
  // the focused card; the visual hierarchy is driven by shadow depth below.
  const scale = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [0.85, 0.95, 1, 0.95, 0.85],
  );
  // Shadow grows from "flat against the page" at the edges into a deep
  // navy-tinted drop at center. The focused card visually lifts off the
  // surface; neighbors sit flush, anchoring the wheel.
  const boxShadow = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [
      '0 0 0 0 rgba(0, 51, 73, 0)',
      '0 6px 20px -4px rgba(0, 51, 73, 0.25)',
      '0 24px 50px -8px rgba(0, 51, 73, 0.4)',
      '0 6px 20px -4px rgba(0, 51, 73, 0.25)',
      '0 0 0 0 rgba(0, 51, 73, 0)',
    ],
  );

  return (
    <section
      ref={ref}
      data-snap-page
      data-layout-index={index}
      className="h-[min(60vh,580px)] min-h-[520px] flex items-center justify-center px-8 relative"
      style={{ perspective: 2000 }}
    >
      <PageCard
        layout={layout}
        rotateX={rotateX}
        y={y}
        opacity={opacity}
        scale={scale}
        boxShadow={boxShadow}
        pageIndex={pageIndex}
        totalPages={totalPages}
        scrollYProgress={scrollYProgress}
      />
    </section>
  );
}

function PageCard({
  layout,
  rotateX,
  y,
  opacity,
  scale,
  boxShadow,
  pageIndex,
  totalPages,
  scrollYProgress,
}: {
  layout: LayoutListItem;
  rotateX: MotionValue<number>;
  y: MotionValue<number>;
  opacity: MotionValue<number>;
  scale: MotionValue<number>;
  boxShadow: MotionValue<string>;
  pageIndex: number;
  totalPages: number;
  scrollYProgress: MotionValue<number>;
}) {
  const counterOpacity = useTransform(scrollYProgress, [0.35, 0.5, 0.65], [0, 1, 0]);
  return (
    <motion.div
      style={{ rotateX, y, opacity, scale, boxShadow, transformStyle: 'preserve-3d' }}
      className="w-full max-w-2xl rounded-2xl overflow-hidden"
    >
      <div className="block relative w-full">
        <PlaceholderRender
          aspectClass="aspect-[4/3]"
          label={`#${formatCode(layout.code)}`}
          src={layout.heroUrl}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 mb-1">
            {layout.familyName}
          </p>
          <h2 className="text-2xl font-semibold text-white leading-tight">
            #{formatCode(layout.code)}
          </h2>
          <p className="text-white/60 text-xs mt-1">
            {formatDimensions(layout.widthInches, layout.depthInches)} · {layout.itemCount} items
          </p>
        </div>
        <motion.div
          style={{ opacity: counterOpacity }}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] text-white/50 tracking-[0.35em] pointer-events-none select-none"
        >
          {pageIndex} / {totalPages}
        </motion.div>
      </div>
    </motion.div>
  );
}

function FocusPanel({ layout }: { layout: LayoutListItem | null }) {
  return (
    <div className="h-full overflow-hidden bg-white border-l border-vari-soft-gray flex flex-col">
      {/* Direct swap, no AnimatePresence mode="wait": serialized exit->enter
          transitions deadlock the panel when the focused layout changes faster
          than the animation (fast scrolling), leaving stale info on the right.
          FocusedLayoutView still animates in on mount (keyed by layout code). */}
      {layout ? (
        <FocusedLayoutView key={layout.code} layout={layout} />
      ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="h-full flex items-center justify-center px-10 text-center"
          >
            <div className="space-y-3 max-w-sm">
              <p className="text-xs uppercase tracking-[0.3em] text-vari-blue-accessible">
                Idea Starter
              </p>
              <p className="text-2xl font-semibold text-vari-navy tracking-tight">
                Scroll to browse layouts
              </p>
              <p className="text-vari-gray text-sm">
                Each layout's renders and pricing will appear here as you flip through the catalog
                on the left.
              </p>
            </div>
          </motion.div>
        )}
    </div>
  );
}

/**
 * The right-side panel content for one focused layout. Header + renders +
 * inline BOM (fetched on focus change). The middle content area scrolls; the
 * Total / Add-to-deck bar stays pinned to the bottom so it's always reachable.
 */
function FocusedLayoutView({ layout }: { layout: LayoutListItem }) {
  const [detail, setDetail] = useState<LayoutDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    fetchLayout(layout.code)
      .then((r) => {
        if (!cancelled) setDetail(r.layout);
      })
      .catch(() => {
        // Keep null — BOM section will show its loading/empty fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [layout.code]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      className="h-full flex flex-col"
    >
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-8 lg:px-10 pt-5 pb-6 space-y-4">
        {/* Compact header: code + family left, dims right */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-vari-blue-accessible">
              {layout.familyName}
            </p>
            <h2 className="text-2xl font-semibold text-vari-navy tracking-tight mt-0.5">
              #{formatCode(layout.code)}
            </h2>
          </div>
          <p className="text-xs text-vari-gray text-right shrink-0 mt-1 leading-relaxed">
            {formatDimensions(layout.widthInches, layout.depthInches)}<br />
            {layout.itemCount} items
          </p>
        </div>

        {/* Renders: click a single tile to add JUST that photo to the deck */}
        <RenderSelectionGrid layout={layout} />

        {/* Bill of materials — fetched on focus change */}
        <FocusBom detail={detail} layout={layout} />
      </div>

      {/* Sticky footer: CTA. Stays put while the content above scrolls. */}
      <div className="shrink-0 border-t border-vari-soft-gray bg-white px-8 lg:px-10 py-4 flex justify-end">
        <AddToDeckButton layout={layout} />
      </div>
    </motion.div>
  );
}

function FocusBom({
  detail,
  layout,
}: {
  detail: LayoutDetail | null;
  layout: LayoutListItem;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
        Bill of materials
      </h3>

      {detail === null ? (
        <p className="text-xs text-vari-gray">Loading…</p>
      ) : (
        <BomTable items={detail.items} totalCents={layout.totalPriceCents} />
      )}
    </section>
  );
}

function RenderSelectionGrid({ layout }: { layout: LayoutListItem }) {
  const { isRenderSelected, toggleRenderForLayout } = useDeck();
  const [lightboxKind, setLightboxKind] = useState<RenderKind | null>(null);

  const selectedCount = DECK_RENDER_KINDS.filter((k) => isRenderSelected(layout.code, k)).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
          Renders
        </p>
        {selectedCount > 0 && (
          <p className="text-[11px] text-vari-gray">
            {selectedCount} of {DECK_RENDER_KINDS.length} in deck
          </p>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {GRID_RENDER_KINDS.map((kind) => {
          const selected = isRenderSelected(layout.code, kind);
          const label = RENDER_LABELS[kind as RenderKind];
          return (
            <div
              key={kind}
              className={`relative rounded-md overflow-hidden border-2 transition-all ${
                selected ? 'border-vari-blue-accessible' : 'border-vari-soft-gray'
              }`}
            >
              {/* Click photo to expand */}
              <button
                type="button"
                onClick={() => setLightboxKind(kind)}
                className="block w-full cursor-pointer"
                title={`View ${label}`}
              >
                <PlaceholderRender
                  variant="card"
                  label={label}
                  src={layout.renders[kind]?.thumbUrl ?? null}
                  fit={kind === 'dimensional' ? 'contain' : 'cover'}
                />
              </button>

              {/* Deck toggle — environmental renders only (decks are env-only) */}
              {DECK_RENDER_KINDS.includes(kind) && (
                <button
                  type="button"
                  onClick={() => toggleRenderForLayout(layout, kind)}
                  aria-pressed={selected}
                  title={selected ? `Remove ${label} from deck` : `Add ${label} to deck`}
                  className={`absolute top-2 right-2 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all shadow ${
                    selected
                      ? 'bg-vari-navy border-vari-navy text-white'
                      : 'bg-white/80 border-vari-soft-gray hover:border-vari-navy text-transparent'
                  }`}
                >
                  <svg viewBox="0 0 10 10" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1.5 5l2.5 2.5 4.5-4" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {lightboxKind !== null && (
          <Lightbox
            src={layout.renders[lightboxKind]?.fullUrl ?? null}
            label={RENDER_LABELS[lightboxKind]}
            onClose={() => setLightboxKind(null)}
            onPrev={() =>
              setLightboxKind((k) => {
                const i = GRID_RENDER_KINDS.indexOf(k as RenderKind);
                return GRID_RENDER_KINDS[(i - 1 + GRID_RENDER_KINDS.length) % GRID_RENDER_KINDS.length] ?? k;
              })
            }
            onNext={() =>
              setLightboxKind((k) => {
                const i = GRID_RENDER_KINDS.indexOf(k as RenderKind);
                return GRID_RENDER_KINDS[(i + 1) % GRID_RENDER_KINDS.length] ?? k;
              })
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
}
